<script lang="ts" setup>
import type { ApiOfficeserviceServices } from '~~/server/api/officeservice/services'
import type { FormKitCustomSelectOption } from '~/components/form-kit/custom/select/FormKitCustomSelect.vue'

export interface WidgetOfficesMapProps {
    data: ApiOfficeserviceServices
}

const { $t } = useNuxtApp()
const route = useRoute()
const router = useRouter()

const title = $t('Servisy')
const serviceWithInspection = $t('Servis s obhliadkou')
const serviceType = $t('Typ servisu')

// Accessibility: aria-live region for announcing checkbox state changes (WCAG 4.1.2, 1.3.1)
const checkboxStatusAnnouncement = ref<string>('')

const { data, sendEvent } = useTracker()

const windshieldKey = 'only_with_windshield'
const { data: dataServices, pending } = useLazyFetch<ApiOfficeserviceServices>(
    () =>
        `/api/officeservice/services?query=${
            route.query?.query || ''
        }&per_page=1000000&page=1&type=${route.query?.type || ''}&inspection=${
            route.query?.inspection || ''
        }&lat=${route.query?.lat || ''}&lng=${route.query?.lng || ''}`,
    {
        server: false,
    }
)
const options = computed<FormKitCustomSelectOption | undefined>(
    (): FormKitCustomSelectOption | undefined => {
        // don't show windshieldKey option when inspection filter is active
        if (dataServices.value?.filter_options && route.query?.inspection === 'true') {
            delete dataServices.value.filter_options[windshieldKey]
            return dataServices.value.filter_options
        }

        if (
            dataServices.value !== undefined &&
            dataServices.value !== null &&
            !dataServices.value.filter_options
        )
            return undefined

        return dataServices.value?.filter_options
    }
)

const updateRoute = ({
    id,
    value,
}: {
    id: string
    value: string | boolean | number
}): void => {
    const query: { [key: string]: any } = {}
    query[id] = value
    router.push({ query: { ...route.query, ...query }, hash: route.hash })
}

const handleFilterChange = (e: Event): void => {
    const target = e.target as HTMLInputElement
    updateRoute({ id: target.id, value: target.checked })

    // Accessibility: Announce checkbox state change to screen readers (WCAG 4.1.2, 1.3.1)
    const filterName = target.dataset.title || ''
    const stateText = target.checked ? $t('začiarknuté') : $t('nezačiarknuté')
    checkboxStatusAnnouncement.value = `${filterName}, ${stateText}`

    // Keep focus on the checkbox after filter change (WCAG 2.4.3)
    // Prevents focus from jumping to the map when data reloads
    nextTick(() => {
        target.focus()
    })

    sendEvent({
        data: {
            ...data,
            tab: title,
            title: target.dataset.title || '',
        },
    })
}

const position = computed<string>(() => {
    if (data.position) {
        return `${data.position} / ${title}`
    }

    return title
})

const handleSelectChange = (value: string | number | boolean) => {
    updateRoute({ id: 'type', value })

    sendEvent({
        data: {
            ...data,
            event: 'select',
            position: position.value,
            title: serviceType,
        },
    })
}
</script>
<template>
  <ClientOnly>
    <Tracker :data="{ position }">
      <OfficesMap
        v-if="dataServices"
        key="widget-service-map"
        :title="title"
        :title-modal="$t('Servis')"
        :pending="pending"
        :data="dataServices"
        named-route-key="service-detail"
        map-height="64rem"
        :position="position"
        type="services"
      >
        <!-- Accessibility: Screen reader announcement for checkbox state changes (WCAG 4.1.2, 1.3.1) -->
        <div
          aria-live="polite"
          aria-atomic="true"
          class="sr-only"
        >
          {{ checkboxStatusAnnouncement }}
        </div>
        <div class="gap-5 formkit-outer">
          <FormKitCustomSelect
            :data="{
              name: 'type',
              list_options: options as any,
              label: serviceType,
              defaultValue: route.query?.type || 'all',
              help: undefined,
              validations: undefined,
              validation_messages: undefined,
            }"
            show-label
            class="mb-3"
            @update="handleSelectChange"
          />
          <label class="d-flex align-items-center gap-2">
            <input
              id="inspection"
              type="checkbox"
              :data-title="serviceWithInspection"
              :disabled="route.query?.type === windshieldKey"
              :checked="route.query?.inspection === 'true'"
              name="inspection"
              @change="handleFilterChange"
            >
            {{ serviceWithInspection }}
          </label>
        </div>
      </OfficesMap>
    </Tracker>
  </ClientOnly>
</template>
<style lang="scss">
@import '~/components/form-kit/wrapper/FormKitWrapper.vars.scss';
</style>
<style lang="scss">
@import '~/components/form-kit/wrapper/FormKitWrapper.scss';
</style>
<style lang="scss" scoped>
// Accessibility: Screen reader only class for aria-live announcements (WCAG 4.1.2)
.sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
}
</style>
